/**
 * AdminHealthTile — surfaces `/api/public/diag` in the admin dashboard.
 *
 * Shows: last auto-sync outcome + relative time, current season, matchweek
 * snapshot count, would-purge stray fixture count, backend version, auto-sync
 * enabled + interval + quiet window.
 */
import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { ArrowsClockwise, Warning, CheckCircle, Pulse } from "@phosphor-icons/react";

function _relative(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "?";
  const diff = Date.now() - then;
  if (diff < 0) return "just now";
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function StatusChip({ status }) {
  if (status === "ok") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5">
        <CheckCircle size={11} weight="fill" /> OK
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-300 border border-red-400/40 bg-red-500/10 px-1.5 py-0.5">
        <Warning size={11} weight="fill" /> Error
      </span>
    );
  }
  if (status === "skipped") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/40 bg-amber-500/10 px-1.5 py-0.5">
        <Pulse size={11} weight="fill" /> Skipped
      </span>
    );
  }
  return (
    <span className="text-[10px] uppercase tracking-widest text-white/40">
      —
    </span>
  );
}

export default function AdminHealthTile() {
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await apiClient.get("/public/diag");
      setDiag(data);
    } catch (e) {
      setErr(e?.message || "Failed to load diag");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, []);

  const auto = diag?.auto_sync;
  const last = auto?.last_run;
  const manual = auto?.last_manual;
  const scrubbed = last?.scrubbed || {};

  return (
    <section
      className="mt-10 surface border border-white/10 p-6"
      data-testid="admin-health-tile"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl uppercase font-black">System Health</h2>
          <p className="text-xs text-white/50 mt-1">
            Public diagnostics — sourced from <span className="font-mono-num text-primary-yellow">/api/public/diag</span>. Auto-refreshes every 60s.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          data-testid="admin-health-refresh"
          className="inline-flex items-center gap-1.5 border border-white/15 text-white/70 uppercase tracking-[0.2em] text-[10px] font-bold px-3 py-2 hover:bg-white/5 disabled:opacity-50"
        >
          <ArrowsClockwise size={12} weight="bold" />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {err && (
        <div className="mt-4 border border-red-400/40 bg-red-500/10 text-red-200 text-sm px-4 py-2">
          {err}
        </div>
      )}

      {diag && (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {/* Last auto-sync */}
          <div className="border border-white/10 p-4 surface-elevated">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-white/50">Last auto-sync</div>
              <StatusChip status={last?.status} />
            </div>
            <div className="mt-2 font-mono-num text-lg font-bold text-white/90" data-testid="health-last-sync-relative">
              {_relative(last?.at)}
            </div>
            <div className="text-[10px] text-white/40 mt-1 font-mono-num truncate">
              {last?.at || "—"}
            </div>
            {last && (
              <div className="mt-3 text-[10px] uppercase tracking-widest text-white/50 space-y-0.5">
                <div>Fetched: <span className="text-white/80 font-mono-num">{last.fetched ?? "—"}</span></div>
                <div>Finished scored: <span className="text-white/80 font-mono-num">{last.finished_synced ?? "—"}</span></div>
                <div>Preds scored: <span className="text-white/80 font-mono-num">{last.predictions_scored ?? "—"}</span></div>
                {last.error && (
                  <div className="text-red-300 truncate" title={last.error}>Error: {last.error}</div>
                )}
              </div>
            )}
          </div>

          {/* Stray fixture scrub */}
          <div className="border border-white/10 p-4 surface-elevated">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-white/50">Stray-fixture scrub</div>
              {(diag.would_purge_count > 0 || (scrubbed.removed_fixtures || 0) > 0) ? (
                <StatusChip status="error" />
              ) : (
                <StatusChip status="ok" />
              )}
            </div>
            <div className="mt-2 font-mono-num text-lg font-bold text-white/90" data-testid="health-would-purge">
              {diag.would_purge_count} <span className="text-xs font-normal text-white/50">to purge</span>
            </div>
            <div className="text-[10px] text-white/40 mt-1">Cutoff: <span className="font-mono-num">{diag.purge_cutoff}</span></div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-white/50 space-y-0.5">
              <div>Last scrub — fixtures: <span className="text-white/80 font-mono-num">{scrubbed.removed_fixtures ?? 0}</span></div>
              <div>Last scrub — preds: <span className="text-white/80 font-mono-num">{scrubbed.removed_predictions ?? 0}</span></div>
              <div>Rejected off-season: <span className="text-white/80 font-mono-num">{last?.rejected_stale ?? 0}</span></div>
            </div>
          </div>

          {/* Auto-sync config */}
          <div className="border border-white/10 p-4 surface-elevated">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-white/50">Auto-sync</div>
              {auto?.enabled ? (
                <StatusChip status="ok" />
              ) : (
                <StatusChip status="skipped" />
              )}
            </div>
            <div className="mt-2 font-mono-num text-lg font-bold text-white/90">
              every {auto?.interval_hours}h
            </div>
            <div className="text-[10px] text-white/40 mt-1 font-mono-num">Provider: {auto?.provider}</div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-white/50 space-y-0.5">
              <div>Quiet window UTC: <span className="text-white/80 font-mono-num">{auto?.quiet_from_utc}:00 – {auto?.quiet_to_utc}:00</span></div>
              <div>Fixtures total: <span className="text-white/80 font-mono-num">{diag.fixtures?.total}</span></div>
              <div>Snapshots ({diag.current_season}): <span className="text-white/80 font-mono-num">{diag.matchweek_snapshots ?? 0}</span></div>
            </div>
          </div>

          {/* Last manual sync */}
          {manual && (
            <div className="border border-white/10 p-4 surface-elevated sm:col-span-2 lg:col-span-3">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest text-white/50">
                  Last MANUAL sync <span className="text-white/30">· {manual.mode}</span>
                </div>
                <StatusChip status={manual.status} />
              </div>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-white/70 font-mono-num">
                <span>at {_relative(manual.at)}</span>
                <span>fetched {manual.fetched ?? "—"}</span>
                <span>added {manual.added ?? "—"}</span>
                <span>updated {manual.updated ?? "—"}</span>
                <span>finished {manual.finished_synced ?? "—"}</span>
                <span>preds {manual.predictions_scored ?? "—"}</span>
              </div>
            </div>
          )}

          {/* Version footer */}
          <div className="sm:col-span-2 lg:col-span-3 text-[10px] uppercase tracking-widest text-white/40 border-t border-white/5 pt-3 flex flex-wrap gap-4">
            <span>backend: <span className="text-primary-yellow font-mono-num">{diag.backend_version}</span></span>
            <span>season: <span className="font-mono-num text-white/70">{diag.current_season}</span> (env {diag.env_default_season})</span>
            <span>virtuals: <span className="font-mono-num text-white/70">{diag.virtual_users}</span></span>
            <span>default 0-0 preds: <span className="font-mono-num text-white/70">{diag.default_predictions}</span></span>
          </div>
        </div>
      )}
    </section>
  );
}
